import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast, Button, SimpleTable, StatusBadge } from '@src/components/ui';
import { RichTextEditor } from '@src/components/RichTextEditor';
import AdminLayout from '../AdminLayout';
import { getBooleanStatusBadgeColors } from '../utils/formatters';
import { fetchBioPageAdmin, updateBioIntro, createBioCategory, updateBioCategory, deleteBioCategory, reorderBioCategories, createBioMember, updateBioMember, deleteBioMember, reorderBioMembers, } from '../../../api/BioPageAdminApi';
import { validateProfileImage } from '../../../utils/imageConstraints';
import { normalizeBioPage, normalizeMember } from './types';
import { createBioMemberColumns } from './tableColumns';
import styles from './BioPage.module.scss';
const EMPTY_MEMBER_FORM = {
    name: '',
    credentials: '',
    title: '',
    biography: '',
    is_active: true,
};
const getBioErrorMessage = (error, fallback) => {
    const payload = error?.response?.body;
    if (typeof payload?.detail === 'string' && payload.detail.trim())
        return payload.detail;
    const nonFieldMessage = payload?.nonFieldErrors?.[0] ?? payload?.non_field_errors?.[0];
    if (typeof nonFieldMessage === 'string' && nonFieldMessage.trim())
        return nonFieldMessage;
    const fieldMessage = payload?.name?.[0] ?? payload?.title?.[0] ?? payload?.biography?.[0] ??
        payload?.profileImage?.[0] ?? payload?.profile_image?.[0];
    if (typeof fieldMessage === 'string' && fieldMessage.trim())
        return fieldMessage;
    return fallback;
};
/**
 * Two API paths return different shapes: a plain JSON create/update
 * resolves via superagent with `.body` already parsed (and superagent
 * itself rejects the promise on a non-2xx status, so a resolved
 * superagent response always means success), while a multipart (file)
 * create/update is a raw fetch() Response the caller must parse and
 * .ok-check itself - see ProductCategoryApi.ts for the precedent.
 *
 * Note: superagent's Response also exposes a boolean `.ok` (true for any
 * 2xx), so `.ok` alone can't distinguish the two - only a native fetch()
 * Response has a `.json()` method.
 */
const parseMemberResponse = async (response) => {
    if (response && typeof response.json === 'function') {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw { response: { body } };
        }
        return body;
    }
    return response.body;
};
const BioPage = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [bioPage, setBioPage] = useState(null);
    const latestRequestId = useRef(0);
    const [introDraft, setIntroDraft] = useState('');
    const [isSavingIntro, setIsSavingIntro] = useState(false);
    const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryNameDraft, setCategoryNameDraft] = useState('');
    const [categoryIsActiveDraft, setCategoryIsActiveDraft] = useState(true);
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    const [categoryErrorMessage, setCategoryErrorMessage] = useState('');
    const [isMemberPanelOpen, setIsMemberPanelOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [memberCategoryId, setMemberCategoryId] = useState('');
    const [memberForm, setMemberForm] = useState(EMPTY_MEMBER_FORM);
    const [memberImageFile, setMemberImageFile] = useState(null);
    const [memberImagePreview, setMemberImagePreview] = useState('');
    const [removeMemberImage, setRemoveMemberImage] = useState(false);
    const [isSavingMember, setIsSavingMember] = useState(false);
    const [memberErrorMessage, setMemberErrorMessage] = useState('');
    const loadBioPage = useCallback(async () => {
        const requestId = ++latestRequestId.current;
        try {
            setLoading(true);
            const response = await fetchBioPageAdmin();
            if (requestId !== latestRequestId.current)
                return;
            const normalized = normalizeBioPage(response.body);
            setBioPage(normalized);
            setIntroDraft(normalized.intro_content);
        }
        catch (error) {
            if (requestId !== latestRequestId.current)
                return;
            console.error('Error loading bio page:', error);
            showToast({ message: 'Failed to load bio page', variant: 'error', noBorder: true });
        }
        finally {
            if (requestId === latestRequestId.current)
                setLoading(false);
        }
    }, [showToast]);
    useEffect(() => {
        loadBioPage();
    }, [loadBioPage]);
    const sortedCategories = bioPage
        ? [...bioPage.categories].sort((a, b) => a.sort_order - b.sort_order)
        : [];
    // --- Intro content ---
    const handleSaveIntro = async () => {
        setIsSavingIntro(true);
        try {
            await updateBioIntro(introDraft);
            setBioPage((prev) => (prev ? { ...prev, intro_content: introDraft } : prev));
            showToast({ message: 'Intro content updated successfully', variant: 'success', noBorder: true });
        }
        catch (error) {
            console.error('Error updating intro content:', error);
            showToast({ message: getBioErrorMessage(error, 'Failed to update intro content'), variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingIntro(false);
        }
    };
    // --- Categories ---
    const handleOpenAddCategory = () => {
        setEditingCategory(null);
        setCategoryNameDraft('');
        setCategoryIsActiveDraft(true);
        setCategoryErrorMessage('');
        setIsCategoryPanelOpen(true);
    };
    const handleOpenEditCategory = (category) => {
        setEditingCategory(category);
        setCategoryNameDraft(category.name);
        setCategoryIsActiveDraft(category.is_active);
        setCategoryErrorMessage('');
        setIsCategoryPanelOpen(true);
    };
    const handleCloseCategoryPanel = () => {
        setIsCategoryPanelOpen(false);
        setEditingCategory(null);
        setCategoryNameDraft('');
        setCategoryIsActiveDraft(true);
        setCategoryErrorMessage('');
    };
    const handleSaveCategory = async () => {
        if (!categoryNameDraft.trim()) {
            setCategoryErrorMessage('Category name is required');
            return;
        }
        setIsSavingCategory(true);
        setCategoryErrorMessage('');
        try {
            if (editingCategory) {
                await updateBioCategory(editingCategory.id, {
                    name: categoryNameDraft.trim(),
                    is_active: categoryIsActiveDraft,
                });
                setBioPage((prev) => (prev ? {
                    ...prev,
                    categories: prev.categories.map((category) => (category.id === editingCategory.id
                        ? { ...category, name: categoryNameDraft.trim(), is_active: categoryIsActiveDraft }
                        : category)),
                } : prev));
                showToast({ message: 'Category updated successfully', variant: 'success', noBorder: true });
            }
            else {
                const response = await createBioCategory({
                    name: categoryNameDraft.trim(),
                    is_active: categoryIsActiveDraft,
                });
                const created = {
                    id: response.body.id,
                    bio_page: response.body.bioPage ?? response.body.bio_page,
                    name: response.body.name,
                    sort_order: response.body.sortOrder ?? response.body.sort_order ?? 0,
                    is_active: !!(response.body.isActive ?? response.body.is_active),
                    members: [],
                };
                setBioPage((prev) => (prev ? { ...prev, categories: [...prev.categories, created] } : prev));
                showToast({ message: 'Category created successfully', variant: 'success', noBorder: true });
            }
            handleCloseCategoryPanel();
        }
        catch (error) {
            const message = getBioErrorMessage(error, 'Failed to save category');
            setCategoryErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingCategory(false);
        }
    };
    const handleDeleteCategory = async (category) => {
        if (!window.confirm(`Delete category "${category.name}"?`))
            return;
        try {
            await deleteBioCategory(category.id);
            setBioPage((prev) => (prev ? {
                ...prev,
                categories: prev.categories.filter((item) => item.id !== category.id),
            } : prev));
            showToast({ message: 'Category deleted successfully', variant: 'success', noBorder: true });
        }
        catch (error) {
            console.error('Error deleting category:', error);
            showToast({ message: getBioErrorMessage(error, 'Failed to delete category'), variant: 'error', noBorder: true });
        }
    };
    const moveCategory = async (category, direction) => {
        const index = sortedCategories.findIndex((item) => item.id === category.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (index === -1 || swapIndex < 0 || swapIndex >= sortedCategories.length)
            return;
        const other = sortedCategories[swapIndex];
        try {
            await reorderBioCategories([
                { id: category.id, sort_order: other.sort_order },
                { id: other.id, sort_order: category.sort_order },
            ]);
            setBioPage((prev) => (prev ? {
                ...prev,
                categories: prev.categories.map((item) => {
                    if (item.id === category.id)
                        return { ...item, sort_order: other.sort_order };
                    if (item.id === other.id)
                        return { ...item, sort_order: category.sort_order };
                    return item;
                }),
            } : prev));
        }
        catch (error) {
            console.error('Error reordering categories:', error);
            showToast({ message: 'Failed to reorder categories', variant: 'error', noBorder: true });
        }
    };
    // --- Members ---
    const handleOpenAddMember = (category) => {
        setEditingMember(null);
        setMemberCategoryId(category.id);
        setMemberForm(EMPTY_MEMBER_FORM);
        setMemberImageFile(null);
        setMemberImagePreview('');
        setRemoveMemberImage(false);
        setMemberErrorMessage('');
        setIsMemberPanelOpen(true);
    };
    const handleOpenEditMember = (category, member) => {
        setEditingMember(member);
        setMemberCategoryId(category.id);
        setMemberForm({
            name: member.name,
            credentials: member.credentials,
            title: member.title,
            biography: member.biography,
            is_active: member.is_active,
        });
        setMemberImageFile(null);
        setMemberImagePreview(member.profile_image_url || '');
        setRemoveMemberImage(false);
        setMemberErrorMessage('');
        setIsMemberPanelOpen(true);
    };
    const handleCloseMemberPanel = () => {
        setIsMemberPanelOpen(false);
        setEditingMember(null);
        setMemberCategoryId('');
        setMemberForm(EMPTY_MEMBER_FORM);
        setMemberImageFile(null);
        setMemberImagePreview('');
        setRemoveMemberImage(false);
        setMemberErrorMessage('');
    };
    const handleMemberImageChange = (file) => {
        if (file) {
            const validationError = validateProfileImage(file);
            if (validationError) {
                setMemberErrorMessage(validationError);
                showToast({ message: validationError, variant: 'error', noBorder: true });
                return;
            }
        }
        setMemberImageFile(file);
        setRemoveMemberImage(false);
        setMemberErrorMessage('');
        if (!file) {
            setMemberImagePreview(editingMember?.profile_image_url || '');
            return;
        }
        setMemberImagePreview(URL.createObjectURL(file));
    };
    const handleRemoveMemberImage = () => {
        setMemberImageFile(null);
        setMemberImagePreview('');
        setRemoveMemberImage(true);
    };
    const handleSaveMember = async () => {
        if (!memberForm.name.trim() || !memberForm.title.trim() || !memberForm.biography.trim()) {
            setMemberErrorMessage('Name, title, and biography are required');
            return;
        }
        setIsSavingMember(true);
        setMemberErrorMessage('');
        try {
            const basePayload = {
                name: memberForm.name.trim(),
                credentials: memberForm.credentials.trim(),
                title: memberForm.title.trim(),
                biography: memberForm.biography,
                is_active: memberForm.is_active,
                ...(memberImageFile ? { profile_image: memberImageFile } : {}),
                ...(removeMemberImage ? { remove_profile_image: true } : {}),
            };
            let normalized;
            if (editingMember) {
                const rawResponse = await updateBioMember(editingMember.id, basePayload);
                const body = await parseMemberResponse(rawResponse);
                normalized = normalizeMember(body);
            }
            else {
                const rawResponse = await createBioMember({
                    bio_category: memberCategoryId,
                    ...basePayload,
                });
                const body = await parseMemberResponse(rawResponse);
                normalized = normalizeMember(body);
            }
            setBioPage((prev) => {
                if (!prev)
                    return prev;
                return {
                    ...prev,
                    categories: prev.categories.map((category) => {
                        if (category.id !== memberCategoryId)
                            return category;
                        const exists = category.members.some((member) => member.id === normalized.id);
                        return {
                            ...category,
                            members: exists
                                ? category.members.map((member) => (member.id === normalized.id ? normalized : member))
                                : [...category.members, normalized],
                        };
                    }),
                };
            });
            showToast({
                message: editingMember ? 'Member updated successfully' : 'Member created successfully',
                variant: 'success',
                noBorder: true,
            });
            handleCloseMemberPanel();
        }
        catch (error) {
            const message = getBioErrorMessage(error, 'Failed to save member');
            setMemberErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingMember(false);
        }
    };
    const handleDeleteMember = async (category, member) => {
        if (!window.confirm(`Delete "${member.name}"?`))
            return false;
        try {
            await deleteBioMember(member.id);
            setBioPage((prev) => (prev ? {
                ...prev,
                categories: prev.categories.map((item) => (item.id === category.id
                    ? { ...item, members: item.members.filter((m) => m.id !== member.id) }
                    : item)),
            } : prev));
            showToast({ message: 'Member deleted successfully', variant: 'success', noBorder: true });
            return true;
        }
        catch (error) {
            console.error('Error deleting member:', error);
            showToast({ message: getBioErrorMessage(error, 'Failed to delete member'), variant: 'error', noBorder: true });
            return false;
        }
    };
    const handleDeleteEditingMember = async () => {
        const category = sortedCategories.find((item) => item.id === memberCategoryId);
        if (!editingMember || !category)
            return;
        const deleted = await handleDeleteMember(category, editingMember);
        if (deleted)
            handleCloseMemberPanel();
    };
    const moveMember = async (category, member, direction) => {
        const members = [...category.members].sort((a, b) => a.sort_order - b.sort_order);
        const index = members.findIndex((item) => item.id === member.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (index === -1 || swapIndex < 0 || swapIndex >= members.length)
            return;
        const other = members[swapIndex];
        try {
            await reorderBioMembers([
                { id: member.id, sort_order: other.sort_order },
                { id: other.id, sort_order: member.sort_order },
            ]);
            setBioPage((prev) => (prev ? {
                ...prev,
                categories: prev.categories.map((item) => {
                    if (item.id !== category.id)
                        return item;
                    return {
                        ...item,
                        members: item.members.map((m) => {
                            if (m.id === member.id)
                                return { ...m, sort_order: other.sort_order };
                            if (m.id === other.id)
                                return { ...m, sort_order: member.sort_order };
                            return m;
                        }),
                    };
                }),
            } : prev));
        }
        catch (error) {
            console.error('Error reordering members:', error);
            showToast({ message: 'Failed to reorder members', variant: 'error', noBorder: true });
        }
    };
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.pageWrapper, children: [_jsxs("section", { className: styles.introSection, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { children: "Intro Content" }) }), _jsx(RichTextEditor, { value: introDraft, onChange: setIntroDraft, label: "Intro Content", id: "bio-intro-content" }), _jsx("div", { className: styles.introActions, children: _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingIntro ? 'Saving...' : 'Save Intro', onClick: handleSaveIntro, disabled: isSavingIntro }) })] }), _jsxs("section", { className: styles.categoriesSection, children: [_jsxs("div", { className: styles.sectionHeader, children: [_jsx("h2", { children: "Categories" }), _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Category", onClick: handleOpenAddCategory })] }), sortedCategories.map((category, index) => {
                                const sortedMembers = [...category.members].sort((a, b) => a.sort_order - b.sort_order);
                                const { background, text } = getBooleanStatusBadgeColors(category.is_active);
                                return (_jsxs("div", { className: styles.categoryBlock, children: [_jsxs("div", { className: styles.categoryHeader, children: [_jsxs("div", { className: styles.categoryTitleRow, children: [_jsxs("div", { className: styles.reorderGroup, children: [_jsx("button", { type: "button", className: styles.reorderButton, "aria-label": `Move ${category.name} up`, title: `Move ${category.name} up`, disabled: index === 0, onClick: () => moveCategory(category, 'up'), children: "\u25B2" }), _jsx("button", { type: "button", className: styles.reorderButton, "aria-label": `Move ${category.name} down`, title: `Move ${category.name} down`, disabled: index === sortedCategories.length - 1, onClick: () => moveCategory(category, 'down'), children: "\u25BC" })] }), _jsx("h3", { children: category.name }), _jsx(StatusBadge, { label: category.is_active ? 'Active' : 'Inactive', bgColor: background, color: text })] }), _jsxs("div", { className: styles.categoryActions, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Edit", onClick: () => handleOpenEditCategory(category) }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Delete", onClick: () => handleDeleteCategory(category) })] })] }), _jsx(SimpleTable, { columns: createBioMemberColumns({
                                                onMoveUp: (member) => moveMember(category, member, 'up'),
                                                onMoveDown: (member) => moveMember(category, member, 'down'),
                                                isFirst: (member) => sortedMembers[0]?.id === member.id,
                                                isLast: (member) => sortedMembers[sortedMembers.length - 1]?.id === member.id,
                                            }), data: sortedMembers, tableHeading: `${category.name} Members`, onRowClick: (member) => handleOpenEditMember(category, member), customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Member", onClick: () => handleOpenAddMember(category) }) })] }, category.id));
                            })] })] }), isCategoryPanelOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseCategoryPanel }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: editingCategory ? 'Edit Category' : 'Add Category' }) }), _jsxs("div", { className: styles.slideOutContent, children: [!!categoryErrorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: categoryErrorMessage })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "bio-category-name", children: "Name" }), _jsx("input", { id: "bio-category-name", value: categoryNameDraft, onChange: (event) => {
                                                    setCategoryNameDraft(event.target.value);
                                                    if (categoryErrorMessage)
                                                        setCategoryErrorMessage('');
                                                } })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: categoryIsActiveDraft, onChange: (event) => setCategoryIsActiveDraft(event.target.checked) }), "Is Active"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseCategoryPanel }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingCategory ? 'Saving...' : 'Save Category', onClick: handleSaveCategory, disabled: isSavingCategory })] })] })] })), isMemberPanelOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseMemberPanel }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: editingMember ? 'Edit Member' : 'Add Member' }) }), _jsxs("div", { className: styles.slideOutContent, children: [!!memberErrorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: memberErrorMessage })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "bio-member-name", children: "Name" }), _jsx("input", { id: "bio-member-name", value: memberForm.name, onChange: (event) => setMemberForm((prev) => ({ ...prev, name: event.target.value })) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "bio-member-credentials", children: "Credentials" }), _jsx("input", { id: "bio-member-credentials", value: memberForm.credentials, onChange: (event) => setMemberForm((prev) => ({ ...prev, credentials: event.target.value })) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "bio-member-title", children: "Title" }), _jsx("input", { id: "bio-member-title", value: memberForm.title, onChange: (event) => setMemberForm((prev) => ({ ...prev, title: event.target.value })) })] }), _jsx(RichTextEditor, { value: memberForm.biography, onChange: (html) => setMemberForm((prev) => ({ ...prev, biography: html })), label: "Biography", id: "bio-member-biography" }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "bio-member-image", children: "Profile Image" }), _jsx("input", { id: "bio-member-image", type: "file", accept: "image/*", onChange: (event) => handleMemberImageChange(event.target.files?.[0] || null) }), !!memberImagePreview && (_jsxs("div", { className: styles.imagePreviewWrap, children: [_jsx("img", { src: memberImagePreview, alt: "Profile preview", className: styles.imagePreview }), _jsx(Button, { variant: "secondary", size: "small", label: "Remove image", onClick: handleRemoveMemberImage })] }))] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: memberForm.is_active, onChange: (event) => setMemberForm((prev) => ({ ...prev, is_active: event.target.checked })) }), "Is Active"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [editingMember && (_jsx(Button, { variant: "secondary", size: "small", label: "Delete", onClick: handleDeleteEditingMember })), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseMemberPanel }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingMember ? 'Saving...' : 'Save Member', onClick: handleSaveMember, disabled: isSavingMember })] })] })] }))] }));
};
export default BioPage;
